import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ingredientTransferService } from "~/services/purchasing";
import type { TransferItemInput } from "~/types/ingredientTransfer";
import type { Plant } from "~/types/purchasing";

export const INGREDIENT_TRANSFERS_KEY = ["ingredient_transfers"];

/**
 * List all ingredient transfers (traslados P1 <-> P2), newest first.
 */
export function useIngredientTransfers() {
  return useQuery({
    queryKey: INGREDIENT_TRANSFERS_KEY,
    queryFn: () => ingredientTransferService.listTransfers(),
  });
}

/**
 * Fetch a single transfer with its line items.
 */
export function useIngredientTransfer(id: string) {
  return useQuery({
    queryKey: [...INGREDIENT_TRANSFERS_KEY, id],
    queryFn: () => ingredientTransferService.getTransferById(id),
    enabled: !!id,
  });
}

interface CreateTransferVars {
  fromPlant: Plant;
  toPlant: Plant;
  items: TransferItemInput[];
  notes?: string | null;
  performedBy: string;
}

/**
 * Create a transfer. On success, invalidates the transfer list plus every
 * stock surface the move touched (per-plant rows, global caches, movements).
 */
export function useCreateIngredientTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateTransferVars) =>
      ingredientTransferService.createTransfer(vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INGREDIENT_TRANSFERS_KEY });
      // per-plant stock (source-of-truth) shown on the transfer form + stock page
      queryClient.invalidateQueries({ queryKey: ["oil_type_stock_by_plant"] });
      queryClient.invalidateQueries({
        queryKey: ["raw_material_stock_by_plant"],
      });
      // denormalized global caches (Stock Ingredientes totals)
      queryClient.invalidateQueries({ queryKey: ["oil_types"] });
      queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
      // movement history (the two transfer legs)
      queryClient.invalidateQueries({ queryKey: ["stock_movements"] });
    },
  });
}
